/**
 * Translates semantic operational mode into VoiceBot activation payloads.
 * Transport mechanism is an implementation detail (URL today; swappable later).
 */

import { VOICEBOT_MODES } from './voiceBotOperationalMode';
import {
  CLASSIFIER_MODES,
  normalizeClassifierMode,
  normalizeRenderFps,
  normalizeVerifiedMix,
  normalizeLegacyGearPasser,
  normalizeLegacyVisemeFilter,
  normalizeLegacyConsonantHolder,
  normalizeFaceMorph,
} from './voiceBotExperimentConfig';
import {
  ENVELOPE_SIM_PROFILES,
  isSimPressureAllowed,
  normalizeSimProfile,
  pressureForProfile,
} from './voiceBotEnvelopeConfig';

/** VoiceBot-internal query keys — never imported by React UI components. */
const BENCH_PARAM_ALLOWLIST = Object.freeze([
  'vbBench',
  'vbRecord',
  'vbReplay',
  'vbBaseline',
  'vbOverhead',
  'vbReport',
  'vbStore',
  'vbPanel',
  'vbDisable',
  'vbRegressionMode',
  'vbZSphereStride',
  'vbYtMixMode',
  'vbClassifier',
  'vbRenderFps',
  'vbVerifiedMix',
  'vbLegacyGearPasser',
  'vbLegacyVisemeFilter',
  'vbLegacyConsonantHolder',
  'vbFaceMorph',
  'vbEnvelope',
  'vbSimMs',
]);

const MODE_TO_URL_PARAMS = Object.freeze({
  [VOICEBOT_MODES.PRODUCTION]: {},
  [VOICEBOT_MODES.BENCH]: { vbBench: '1' },
  [VOICEBOT_MODES.RECORD]: { vbRecord: '1' },
  [VOICEBOT_MODES.REPLAY]: { vbReplay: '1' },
  [VOICEBOT_MODES.BASELINE]: { vbBaseline: '1' },
  [VOICEBOT_MODES.OVERHEAD]: { vbOverhead: '1' },
});

function applyAllowlistedParams(url, params) {
  Object.entries(params || {}).forEach(([key, value]) => {
    if (!BENCH_PARAM_ALLOWLIST.includes(key)) return;
    if (value != null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });
}

export function buildActivation(mode, options = {}) {
  const params = { ...(MODE_TO_URL_PARAMS[mode] || {}) };
  if (options.includePanel && mode !== VOICEBOT_MODES.PRODUCTION) {
    params.vbPanel = '1';
  }
  const regressionMode = options.regressionMode === 'algorithm' ? 'algorithm' : 'full';
  if (mode === VOICEBOT_MODES.RECORD || mode === VOICEBOT_MODES.REPLAY) {
    params.vbRegressionMode = regressionMode;
  }

  const classifierMode = normalizeClassifierMode(options.classifierMode);
  const renderFps = normalizeRenderFps(options.renderFps);
  const verifiedMix = normalizeVerifiedMix(options.verifiedMix);
  const legacyGearPasser = normalizeLegacyGearPasser(options.legacyGearPasser);
  const legacyVisemeFilter = normalizeLegacyVisemeFilter(options.legacyVisemeFilter);
  const legacyConsonantHolder = normalizeLegacyConsonantHolder(options.legacyConsonantHolder);
  const faceMorph = normalizeFaceMorph(options.faceMorph);
  const avatarParams = {};
  if (classifierMode !== CLASSIFIER_MODES.LEGACY) {
    avatarParams.vbClassifier = classifierMode;
  }
  if (renderFps === 30 || renderFps === 60) {
    avatarParams.vbRenderFps = String(renderFps);
  }
  if (verifiedMix) {
    avatarParams.vbVerifiedMix = '1';
  }
  // ON is production default — omit. OFF is the experimental bypass.
  if (!legacyGearPasser) {
    avatarParams.vbLegacyGearPasser = '0';
  }
  if (!legacyVisemeFilter) {
    avatarParams.vbLegacyVisemeFilter = '0';
  }
  if (!legacyConsonantHolder) {
    avatarParams.vbLegacyConsonantHolder = '0';
  }
  if (faceMorph) {
    avatarParams.vbFaceMorph = '1';
  }

  const envelopeEnabled = !!options.envelopeEnabled;
  if (envelopeEnabled) {
    params.vbEnvelope = '1';
    params.vbPanel = '1';
  }

  const simProfile = normalizeSimProfile(options.simProfile);
  const simMs = isSimPressureAllowed() ? pressureForProfile(simProfile).busyMs : 0;
  if (simMs > 0) {
    avatarParams.vbSimMs = String(simMs);
  }

  return {
    transport: 'url',
    mode,
    params,
    avatarParams,
    classifierMode,
    renderFps,
    verifiedMix,
    legacyGearPasser,
    legacyVisemeFilter,
    legacyConsonantHolder,
    faceMorph,
    envelopeEnabled,
    simProfile: simMs > 0 ? simProfile : ENVELOPE_SIM_PROFILES.OFF,
    simMs,
  };
}

/**
 * @param {string} baseUrl
 * @param {object} activation
 * @param {{ includeAvatarParams?: boolean }} [options]
 *   Avatar iframe should pass includeAvatarParams: true.
 *   Fluency iframe omits it — classifier, render FPS, verified mix, face morph, and legacy articulation flags are avatar-only.
 */
export function appendActivationToUrl(baseUrl, activation, options = {}) {
  if (!activation || activation.transport !== 'url') {
    return baseUrl;
  }

  const url = new URL(baseUrl);
  applyAllowlistedParams(url, activation.params);
  if (options.includeAvatarParams) {
    applyAllowlistedParams(url, activation.avatarParams);
  }
  return url.toString();
}
